import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router'
import { useParams } from 'react-router-dom'

import { Account } from '@driftt/api-types'

import * as api from 'api'
import { driftLoadingStateBuilder } from 'components/States'
import { AccountDetailsPageParams } from 'hooks/useAccountCompanyTitle'
import { SessionMetrics } from 'hooks/useSessionMetrics'
import { AccountActivity, CompanyData } from 'modules/accountActivity/domain'
import { segmentTrack, TrackType } from 'utils/tracking'
import { getURLSearchParam, getURLSearchParams } from 'utils/urlUtils'

import AccountDetailsHeader from './AccountDetailsHeader'
import AttributesTab from './AttributesTab'
import ContactsTab from './ContactsTab'
import FeedTab from './FeedTab'

import styles from './styles.module.css'

interface AccountDetailsPageProps {
  sessionMetrics: SessionMetrics
}

const TABS = ['Feed', 'Attributes', 'Contacts']
const TAB_SEARCH_PARAM_KEY = 'tab'
const DEFAULT_TAB = 'Feed'

const AccountDetailsPage: React.FC<AccountDetailsPageProps> = ({ sessionMetrics }) => {
  const { id: accountId } = useParams<AccountDetailsPageParams>()
  const history = useHistory()
  const tab = getURLSearchParam(TAB_SEARCH_PARAM_KEY) || DEFAULT_TAB

  const [activeTab, setActiveTab] = useState<string>(tab)
  const [accountData, setAccountData] = useState<Account>()
  const [salesforceData, setSalesforceData] = useState<any>()
  const [companyData, setCompanyData] = useState<CompanyData>()
  const [explainData, setExplainData] = useState<AccountActivity>()

  const [isLoading, setIsLoading] = useState<boolean>(true)

  const LoadingComponent = driftLoadingStateBuilder('Loading account page...')

  const fetchAccountData = async (accountId: string) => {
    try {
      const { data } = await api.getAccountById(accountId)
      setAccountData(data)
    } catch (error) {
      console.error(error) // eslint-disable-line no-console
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAccountExplain = async (accountId: string) => {
    try {
      const { data } = await api.getAccountExplain({ accountId, limit: 10 })
      setCompanyData(data.companyData)
      setExplainData(data)
    } catch (error) {
      console.error(error) // eslint-disable-line no-console
    }
  }

  const fetchSalesforceData = async (domain: string) => {
    try {
      const { data } = await api.fetchSalesforceByDomain(domain)
      setSalesforceData(data)
    } catch (error) {
      console.error(error) // eslint-disable-line no-console
    }
  }

  useEffect(() => {
    document.title = 'Drift | Account Details'
  }, [])

  useEffect(() => {
    const params = getURLSearchParams()
    params.set(TAB_SEARCH_PARAM_KEY, activeTab)
    history.replace({
      search: params.toString(),
      hash: history.location.hash
    })
  }, [history, activeTab])

  useEffect(() => {
    if (accountId) {
      segmentTrack(TrackType.ACCOUNT_DETAILS_PAGE_ACCESSED, {
        accountId,
        companyName: companyData?.name
      })
    }
  }, [accountId, companyData])

  useEffect(() => {
    if (accountId) {
      fetchAccountData(accountId)
      fetchAccountExplain(accountId)
    }
  }, [accountId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (accountData && accountData.domain) {
      fetchSalesforceData(accountData.domain)
    }
  }, [accountData]) // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <div className={styles.emptyPage}>
        <div className={styles.loader}>
          <LoadingComponent />
        </div>
      </div>
    )
  }

  let tabContent = null
  switch (activeTab) {
    case 'Contacts':
      tabContent = <ContactsTab accountId={accountId} accountName={accountData?.name} />
      break
    case 'Attributes':
      tabContent = (
        <AttributesTab
          account={accountData}
          company={companyData}
          sfdcAccounts={salesforceData?.accounts}
          sfdcOpportunities={salesforceData?.opportunities}
        />
      )
      break
    default:
      tabContent = (
        <FeedTab
          sessionMetrics={sessionMetrics}
          salesforceData={salesforceData}
          companyData={companyData}
          explainData={explainData}
        />
      )
  }

  return (
    <div id="accountDetailsPage" className={styles.accountDetailsPage}>
      <div className={styles.header}>
        {
          <AccountDetailsHeader
            activeTab={activeTab}
            tabNames={TABS}
            onTabSelected={setActiveTab}
            account={accountData}
            company={companyData}
            explainData={explainData}
            salesforceData={salesforceData}
          />
        }
      </div>
      <div className={styles.body}>{tabContent}</div>
    </div>
  )
}

export default AccountDetailsPage
